import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { uploadToBucket } from "@/lib/uploads";
import { FileText, Upload, CheckCircle2, Clock, Loader2, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AICallout } from "@/components/makai/primitives";
import { AdaptiveInsightsButton } from "@/components/adaptive/adaptive-insights-button";

export const Route = createFileRoute("/learner/assignments")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Assignments — MAKAI" }] }),
  component: AssignmentsPage,
});

type Assignment = {
  id: string;
  title: string;
  instructions: string | null;
  max_score: number | null;
  due_at: string | null;
  assigned_on: string | null;
  subject: { name: string } | null;
  teacher: { first_name: string | null; last_name: string | null } | null;
};
type Submission = {
  id: string;
  assignment_id: string;
  body: string | null;
  status: string;
  score: number | null;
  feedback: string | null;
  submitted_at: string | null;
};

function AssignmentsPage() {
  const { ctx, loading } = useLearnerContext();
  const [list, setList] = useState<Assignment[]>([]);
  const [subs, setSubs] = useState<Record<string, Submission>>({});
  const [active, setActive] = useState<Assignment | null>(null);
  const [tab, setTab] = useState<"pending" | "submitted" | "graded">("pending");

  useEffect(() => { if (ctx) refresh(); }, [ctx]);

  async function refresh() {
    if (!ctx?.streamId) { setList([]); return; }
    const { data: a } = await supabase.from("assignments")
      .select("id, title, instructions, max_score, due_at, assigned_on, subject:subjects_catalog(name), teacher:staff!assignments_teacher_id_fkey(first_name, last_name)")
      .eq("stream_id", ctx.streamId).order("due_at", { ascending: true, nullsFirst: false });
    setList((a as Assignment[] | null) ?? []);
    const { data: s } = await supabase.from("assignment_submissions")
      .select("id, assignment_id, body, status, score, feedback, submitted_at")
      .eq("learner_id", ctx.learnerId);
    const map: Record<string, Submission> = {};
    ((s ?? []) as Submission[]).forEach((x) => { map[x.assignment_id] = x; });
    setSubs(map);
  }

  if (loading) return <AppShell role="learner"><Spinner /></AppShell>;

  const filtered = list.filter((a) => {
    const sub = subs[a.id];
    if (tab === "pending") return !sub || sub.status === "draft";
    if (tab === "submitted") return sub && (sub.status === "submitted" || sub.status === "late");
    return sub && sub.status === "graded";
  });

  return (
    <AppShell role="learner" title="Assignments" subtitle="Submit work and track feedback.">
      <div className="mb-4 flex gap-2 overflow-x-auto">
        {(["pending", "submitted", "graded"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={cn(
            "shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold capitalize",
            tab === t ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"
          )}>{t}</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Nothing here yet.
          </div>
        )}
        {filtered.map((a) => {
          const sub = subs[a.id];
          const due = a.due_at ? new Date(a.due_at) : null;
          const overdue = due && due.getTime() < Date.now() && !sub;
          return (
            <button key={a.id} onClick={() => setActive(a)} className="block w-full rounded-2xl border border-border bg-card p-4 text-left shadow-soft transition hover:shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                    <h3 className="truncate font-semibold">{a.title}</h3>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {a.subject?.name ?? "General"} · {a.teacher ? `${a.teacher.first_name ?? ""} ${a.teacher.last_name ?? ""}`.trim() : "—"} · {a.max_score ?? 0} marks
                  </p>
                  {a.instructions && <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{a.instructions}</p>}
                </div>
                <div className="shrink-0 text-right">
                  {sub?.status === "graded" ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-2 py-0.5 text-[11px] font-semibold text-primary"><CheckCircle2 className="h-3 w-3" /> {sub.score}/{a.max_score}</span>
                  ) : sub ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-sky/15 px-2 py-0.5 text-[11px] font-semibold text-sky"><CheckCircle2 className="h-3 w-3" /> Submitted</span>
                  ) : overdue ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700"><AlertCircle className="h-3 w-3" /> Overdue</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-md bg-gold/20 px-2 py-0.5 text-[11px] font-semibold text-gold-foreground"><Clock className="h-3 w-3" /> Due {due?.toLocaleDateString() ?? "—"}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {active && (
        <SubmitDialog
          assignment={active}
          submission={subs[active.id]}
          learnerId={ctx!.learnerId}
          schoolId={ctx!.schoolId}
          onClose={() => setActive(null)}
          onDone={async () => { await refresh(); setActive(null); }}
        />
      )}

      <div className="mt-6">
        <AICallout
          description="I can plan your homework week and explain tricky questions."
          suggestions={[
            "What homework is due this week?",
            "Help me solve question 2",
            "Plan my homework time this week",
          ]}
        />
      </div>
    </AppShell>
  );
}

function SubmitDialog({ assignment, submission, learnerId, schoolId, onClose, onDone }: {
  assignment: Assignment; submission?: Submission; learnerId: string; schoolId: string; onClose: () => void; onDone: () => void;
}) {
  const [body, setBody] = useState(submission?.body ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const graded = submission?.status === "graded";

  async function submit() {
    setBusy(true);
    try {
      let attachmentBody = body;
      if (file) {
        const up = await uploadToBucket("submissions", file, `assignments/${assignment.id}`);
        attachmentBody = `${body}\n\n📎 ${up.name}: ${up.url}`.trim();
      }
      const now = new Date().toISOString();
      const due = assignment.due_at ? new Date(assignment.due_at) : null;
      const status = due && new Date() > due ? "late" : "submitted";
      const payload = { assignment_id: assignment.id, learner_id: learnerId, school_id: schoolId, body: attachmentBody, status, submitted_at: now };
      if (submission) {
        await supabase.from("assignment_submissions").update(payload).eq("id", submission.id);
      } else {
        await supabase.from("assignment_submissions").insert(payload);
      }
      toast.success("Submitted!");
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-t-2xl bg-card p-5 shadow-card sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold">{assignment.title}</h3>
            <p className="text-xs text-muted-foreground">{assignment.subject?.name} · Due {assignment.due_at ? new Date(assignment.due_at).toLocaleString() : "—"}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        {assignment.instructions && (
          <div className="mt-4 rounded-xl bg-muted/40 p-3 text-sm whitespace-pre-wrap">{assignment.instructions}</div>
        )}

        {graded ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-primary/30 bg-primary-soft p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-primary">Your score</div>
                  <div className="font-display text-3xl font-bold text-primary">{submission?.score}<span className="text-base text-muted-foreground">/{assignment.max_score}</span></div>
                </div>
                {submission?.score != null && assignment.max_score ? (
                  <AdaptiveInsightsButton
                    learnerId={learnerId}
                    sourceType="assignment"
                    sourceId={assignment.id}
                    subject={assignment.subject?.name ?? "General"}
                    topic={assignment.title}
                    percent={(Number(submission.score) / Number(assignment.max_score)) * 100}
                    totalMarks={Number(assignment.max_score)}
                    earnedMarks={Number(submission.score)}
                    label="Get AI feedback"
                  />
                ) : null}
              </div>
            </div>
            {submission?.feedback && (
              <div className="rounded-xl border border-border p-4">
                <div className="text-xs font-semibold text-muted-foreground">Teacher feedback</div>
                <p className="mt-1 text-sm whitespace-pre-wrap">{submission.feedback}</p>
              </div>
            )}
            {submission?.body && (
              <div className="rounded-xl border border-border p-4">
                <div className="text-xs font-semibold text-muted-foreground">Your submission</div>
                <p className="mt-1 text-sm whitespace-pre-wrap">{submission.body}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Write your answer here..." className="w-full rounded-xl border border-border bg-background p-3 text-sm" />
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-border p-3 text-sm hover:bg-muted/30">
              <Upload className="h-4 w-4" />
              <span className="text-muted-foreground">{file ? file.name : "Attach a file (optional)"}</span>
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-semibold">Cancel</button>
              <button onClick={submit} disabled={busy || (!body && !file)} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {submission ? "Resubmit" : "Submit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return <div className="grid place-items-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
}
